import React, { useContext, useEffect, useState } from "react";
import { Button, Col, DatePicker, Drawer, Form, Input, Row, Upload, Select, Space, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import dayjs from "dayjs";
import { DriverContext } from ".";
import { ApiConfig } from "../../services/ApiConfigs";
import { ApiServices, uploadDocument } from "../../services/apiServices";
import { franchiseService } from "../../services/franchiseService";
import { useSelector } from "react-redux";

const { Option } = Select;

const AddDrivers = () => {
    const { addModalOpen, setAddModalOpen, getUsers, driver, wareHouseData } = useContext(DriverContext);
    const [form] = Form.useForm();
    const isEdit = driver && Object.keys(driver).length > 0;

    const [photoList, setPhotoList] = useState([]);
    const [drivingLicenseList, setDrivingLicenseList] = useState([]);
    const [identityDocuments, setIdentityDocuments] = useState([]);
    const [franchises, setFranchises] = useState([]);
    const [drawerWidth, setDrawerWidth] = useState(window.innerWidth < 640 ? "100%" : 720);

    const userDetail = useSelector((res) => res.UserData?.userData?.user || res.UserData?.userData?.franchise);
    const isFranchise = useSelector((state) => state.UserData?.userData?.role === 'franchise' || state.UserData?.userData?.user?.role === 'franchise');

    useEffect(() => {
        const handleResize = () => {
            setDrawerWidth(window.innerWidth < 640 ? "100%" : 720);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // If admin, fetch franchises for assignment dropdown
    useEffect(() => {
        if (!isFranchise && addModalOpen) {
            franchiseService.list().then(res => {
                if (res?.data && Array.isArray(res.data)) {
                    setFranchises(res.data);
                }
            }).catch((e) => {
                console.error("Failed to fetch franchises:", e);
            });
        }
    }, [isFranchise, addModalOpen]);

    const keepLocalFiles = (fileList) =>
        (Array.isArray(fileList) ? fileList : []).map((f) => ({
            ...f,
            status: "done",
        }));

    useEffect(() => {
        if (!addModalOpen) return;

        if (isEdit) {
            setPhotoList(driver.photoData ? [{ uid: driver.photoData.id, name: driver.photoData.name, url: `${ApiConfig.URL}/${driver.photoData.name}` }] : []);
            setIdentityDocuments(driver.identityDocumentsData ? [{ uid: driver.identityDocumentsData.id, name: driver.identityDocumentsData.name, url: `${ApiConfig.URL}/${driver.identityDocumentsData.name}` }] : []);
            setDrivingLicenseList(driver.documentsData ? [{ uid: driver.documentsData.id, name: driver.documentsData.name, url: `${ApiConfig.URL}/${driver.documentsData.name}` }] : []);
        } else {
            setPhotoList([]);
            setIdentityDocuments([]);
            setDrivingLicenseList([]);
        }

        // Warehouses from DriverWarehouses or legacy
        let warehouseIds = [];
        if (isEdit) {
            if (driver.warehouses && Array.isArray(driver.warehouses)) {
                warehouseIds = driver.warehouses.map(w => (typeof w === 'object' ? w.id : w));
            } else if (driver.warehouse) {
                warehouseIds = String(driver.warehouse).replace(/"/g, '').split(',').map(num => Number(num.trim())).filter(Boolean);
            }
        }

        form.setFieldsValue({
            firstName: isEdit ? driver.firstName || "" : "",
            lastName: isEdit ? driver.lastName || "" : "",
            userName: isEdit ? driver.userName || "" : "",
            email: isEdit ? driver.email || "" : "",
            dob: isEdit && driver.dob ? dayjs(driver.dob, "YYYY-MM-DD") : null,
            phone: isEdit ? driver.phone || "" : "",
            vehicle_number: isEdit ? driver.vehicle_number || "" : "",
            vehicleType: isEdit ? driver.vehicleType || "" : "",
            address: isEdit ? driver.address || "" : (isFranchise ? userDetail?.address || "" : ""),
            city: isEdit ? driver.city || "" : (isFranchise ? userDetail?.city || "" : ""),
            state: isEdit ? driver.state || "" : (isFranchise ? userDetail?.state || "" : ""),
            zip_code: isEdit ? driver.zip_code || "" : (isFranchise ? (userDetail?.pincodes?.[0] || "") : ""),
            warehouse: warehouseIds,
            franchise_id: isEdit ? (driver.franchise_id || null) : (isFranchise ? userDetail?.id : null),
        });
    }, [driver, addModalOpen, isEdit, isFranchise, userDetail]);

    const onClose = () => {
        setAddModalOpen(false);
        form.resetFields();
    };

    const handleEditSubmit = async () => {
        try {
            const values = await form.validateFields();
            delete values.password;
            if (isFranchise) {
                values.franchise_id = userDetail?.id;
            }
            const response = await ApiServices("put", ApiConfig.DRIVER + "/" + driver?.id, values);
            if (response.status === 400 || response.status === 403 || response.status === 500) {
                return message.error(response.message || "Failed to update driver.");
            }
            onClose();
            getUsers();
            message.success("Driver updated successfully!");
        } catch (error) {
            console.error(error);
            message.error(error.message || "Failed to update driver.");
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            values.store_id = userDetail?.storeId;
            if (isFranchise) {
                values.franchise_id = userDetail?.id;
            }
            const response = await ApiServices("post", ApiConfig.ADD_DRIVER, values);
            if (response.status !== 200) {
                return message.error(response.message || "Failed to register driver.");
            }
            const driverId = response.data.id;
            const uploads = {
                photo: photoList[0]?.originFileObj,
                identityDocuments: identityDocuments[0]?.originFileObj,
                drivingLicense: drivingLicenseList[0]?.originFileObj,
            };
            const uploadResults = await Promise.all(
                Object.entries(uploads).map(async ([key, file]) => {
                    if (!file) return null;
                    const res = await uploadDocument(file, key);
                    return res?.data?.data?.id || null;
                })
            );
            if (uploadResults.some(Boolean)) {
                await ApiServices("put", `${ApiConfig.DRIVER}/${driverId}`, {
                    photo: uploadResults[0],
                    identityDocuments: uploadResults[1],
                    documents: uploadResults[2],
                });
            }
            onClose();
            getUsers();
            message.success("Driver registered successfully!");
        } catch (error) {
            console.error("Error:", error);
            message.error(error.message || "Please fill all required fields correctly.");
        }
    };

    return (
        <Drawer
            title={isEdit ? "Edit Driver Account" : "Create a new Driver Account"}
            width={drawerWidth}
            onClose={onClose}
            open={addModalOpen}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={() => {
                        if (isEdit) {
                            handleEditSubmit();
                        } else {
                            handleSubmit();
                        }
                    }}>
                        {isEdit ? "Update" : "Submit"}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
            >
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="firstName"
                            label="First Name"
                            rules={[{ required: true, message: "Please enter First Name" }]}
                        >
                            <Input placeholder="First Name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="lastName"
                            label="Last Name"
                            rules={[{ required: true, message: "Please enter Last Name" }]}
                        >
                            <Input placeholder="Last Name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="userName"
                            label="User Name (Alphabets & Numbers)"
                            rules={[{ required: true, message: "Please enter User Name" }]}
                        >
                            <Input placeholder="User Name" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Email Address"
                            name="email"
                            rules={[
                                { required: true, message: "Please enter your email!" },
                                {
                                    type: "email",
                                    message: "Please enter a valid email address",
                                },
                            ]}
                        >
                            <Input placeholder="Enter email address" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[{ required: !isEdit, message: "Please enter Password" }]}
                        >
                            <Input.Password placeholder="Password" disabled={isEdit} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Date of Birth"
                            name="dob"
                            rules={[
                                { required: true, message: "Please select Date of Birth!" },
                                {
                                    validator: (_, value) =>
                                        value && dayjs().diff(value, "years") >= 18
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("Must be at least 18 years old!")),
                                },
                            ]}
                        >
                            <DatePicker format="YYYY-MM-DD" placeholder="Select DOB" style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="phone"
                            label="Phone"
                            rules={[{ required: true, message: "Please enter a valid phone number!" }]}
                        >
                            <PhoneInput country={"in"} inputStyle={{ width: "100%" }} enableSearch />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="vehicle_number"
                            label="Vehicle Number"
                            rules={[{ required: true, message: "Please enter Vehicle Number" }]}
                        >
                            <Input placeholder="Vehicle Number" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="vehicleType"
                            label="Vehicle Type (Car, Bike, Van, etc.)"
                            rules={[{ required: true, message: "Please enter Vehicle Type" }]}
                        >
                            <Input placeholder="Vehicle Type" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="address"
                            label="Address"
                            rules={[{ required: true, message: 'Please enter Address' }]}
                        >
                            <Input placeholder="Address" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="city"
                            label="City"
                            rules={[{ required: true, message: 'Please enter City' }]}
                        >
                            <Input placeholder="City" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="state"
                            label="State"
                            rules={[{ required: true, message: 'Please enter State' }]}
                        >
                            <Input placeholder="State" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="zip_code"
                            label="Zip Code"
                            rules={[{ required: true, message: 'Please enter Zip Code' }]}
                        >
                            <Input placeholder="Zip Code" />
                        </Form.Item>
                    </Col>

                    {/* Warehouse selection */}
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="warehouse"
                            label="Warehouse"
                            rules={wareHouseData.length > 0 && !isFranchise ? [{ required: true, message: "Please select a warehouse!" }] : []}
                        >
                            <Select
                                mode="multiple"
                                placeholder={wareHouseData.length === 0 ? "No warehouses (Optional)" : "Select Warehouse"}
                                style={{ width: "100%" }}
                                allowClear
                            >
                                {wareHouseData.map((wh) => (
                                    <Option key={wh.id} value={wh.id}>
                                        {wh.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Admin only: Franchise assignment */}
                    {!isFranchise && (
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="franchise_id"
                                label="Assign to Franchise (Optional)"
                            >
                                <Select placeholder="Direct Store (No Franchise)" allowClear>
                                    <Option value={null}>Direct Store (No Franchise)</Option>
                                    {franchises.map((f) => (
                                        <Option key={f.id} value={f.id}>
                                            {f.name} ({f.city || 'Hub'})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    {!isEdit && (
                        <>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Upload Photo" name="photo" rules={[{ required: true, message: "Please upload a photo!" }]}>
                                    <Upload
                                        listType="text"
                                        beforeUpload={() => false}
                                        fileList={photoList}
                                        getValueFromEvent={(e) => e?.fileList}
                                        onChange={async ({ fileList }) => {
                                            setPhotoList(keepLocalFiles(fileList));
                                        }}
                                        maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined />}>Upload</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Upload Driving License" name="documents" rules={[{ required: true, message: "Please upload at least one document!" }]}>
                                    <Upload
                                        listType="text"
                                        beforeUpload={() => false}
                                        fileList={drivingLicenseList}
                                        onChange={({ fileList }) => setDrivingLicenseList(keepLocalFiles(fileList))}
                                        multiple
                                    >
                                        <Button icon={<UploadOutlined />}>Upload</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Identity Documents" name="identityDocuments" rules={[{ required: true, message: "Please upload Identity Documents!" }]}>
                                    <Upload
                                        listType="text"
                                        beforeUpload={() => false}
                                        fileList={identityDocuments}
                                        onChange={({ fileList }) => setIdentityDocuments(keepLocalFiles(fileList))}
                                        maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined />}>Upload</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        </Drawer>
    );
};

export default AddDrivers;
